import React, { useState, useEffect } from 'react';
import usePermissions from 'hooks/permissions-hooks';
import AppLoader from 'components/basic/AppLoader';
import axiosClient from 'http/axios-client';
import CollectionKeys from 'hooks/molecules/collection-keys';
import { useItems } from 'hooks/atoms/items-hook';
import { getFullRegistrationStatus, parseDate } from 'components/business/consents/ConsentsTable';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Select, Typography, FormControl, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Paper, Button, TableSortLabel, TextField, Dialog, DialogActions, DialogContent, DialogContentText } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ManagersRegistrationsDetails from 'components/business/local-manager/ManagersRegistrationsDetails';
import * as XLSX from 'xlsx';
import {
  addDays,
  isWeekend,
  isSameDay,
  getYear,
  parseISO,
  // format,
} from 'date-fns';
import { roles } from '../cassandra-shared-js/roles';

const useStyles = makeStyles({
  title: {
    margin: '0px 0px 5px 5px',
    color: 'rgb(100, 141, 174)',
    fontSize: 35,
  },
  subtitle: {
    margin: '0px 0px 5px 5px',
    color: 'rgb(100, 141, 174)',
    fontSize: 18,
  },
  button: {
    margin: '6px 15px 0px 0px',
    fontSize: 18,
    borderColor: 'rgb(100, 141, 174)',
    color: '#f0f4f8',
    backgroundColor: 'rgb(100, 141, 174)',
    '&:hover': {
      backgroundColor: 'rgb(41, 58, 136)',
    },
  },
  selectLocation: {
    fontSize: 18,
    color: 'rgb(100, 141, 174)',
    margin: '0px 0px 20px 5px',
  },
  searchBox: {
    fontSize: 18,
    color: 'rgb(100, 141, 174)',
    margin: '6px 0px 0px 5px',
  },
  tableContainer: {
    margin: 'auto',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  table: {
    minWidth: 650,
  },
  headerCell: {
    backgroundColor: 'rgb(100, 141, 174)',
    color: '#f0f4f8',
    fontWeight: 600,
    fontSize: 16,
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  infoButton: {
    color: '#ffffff',
    backgroundColor: 'rgb(100, 141, 174)',
    '&:hover': {
      backgroundColor: 'rgb(41, 58, 136)',
    },
  },
  dialogPaper: {
    width: '100%',
    maxWidth: 1200,
  },
  dialogContent: {
    backgroundColor: '#f3f3f3',
  },
});

// calculate the planned delivery day
export function deliveryDate(kit) {
  const sampleReceivedDate = kit?.metadata?.sampleReceivedDate;
  let additionalDays = 20;

  // if it is a pgx kit
  if (kit?.thematic_package?.real_name === 'iDNA PGx CNS Kit') {
    additionalDays = 15;
  }
  // if Albania
  if (kit?.serial_number_id?.country === 'AL') {
    additionalDays = 30;
  }

  function getOrthodoxEasterDate(year) {
    const a = year % 19;
    const b = year % 4;
    const c = year % 7;
    const d = (19 * a + 16) % 30;
    const e = (2 * b + 4 * c + 6 * d) % 7;
    const f = d + e;

    // Orthodox Easter is in April or May
    const day = f + 13; // Julian to Gregorian correction
    const month = f < 18 ? 3 : 4; // April (3) or May (4)

    // Adjust for specific calendar rules
    const date = new Date(year, month, day);

    // Correction for specific years where algorithm shifts by a week
    if (day > 30) {
      date.setMonth(4); // May
      date.setDate(day - 30);
    } else {
      date.setMonth(3); // April
      date.setDate(day);
    }

    return date;
  }

  function getGreekHolidays(year) {
    const easter = getOrthodoxEasterDate(year);

    // Fixed Greek Public Holidays
    const fixedHolidays = [
      `${ year }-01-01`, // New Year's Day
      `${ year }-01-06`, // Epiphany
      `${ year }-03-25`, // Independence Day
      `${ year }-05-01`, // Labour Day
      `${ year }-08-15`, // Assumption of Mary
      `${ year }-10-28`, // Ohi Day
      `${ year }-12-25`, // Christmas
      `${ year }-12-26`, // Second Day of Christmas
    ];

    // Moving Holidays (based on Orthodox Easter)
    // Clean Monday (48 days before Easter)
    const cleanMonday = addDays(easter, -48);
    // Good Friday (2 days before Easter)
    const goodFriday = addDays(easter, -2);
    // Easter Monday (1 day after Easter)
    const easterMonday = addDays(easter, 1);
    // Holy Spirit Day (50 days after Easter)
    const holySpirit = addDays(easter, 50);

    const movingHolidays = [
      cleanMonday,
      goodFriday,
      easterMonday,
      holySpirit,
    ];

    // Combine and return as Date objects
    return [
      ...fixedHolidays.map((d) => { return parseISO(d); }),
      ...movingHolidays,
    ];
  }

  // --- 3. Main Function: Add Working Days ---
  function addWorkingDays(startDate, daysToAdd) {
    let current = parseISO(startDate);
    let added = 0;

    // Get holidays for the current year and next year (to cover the range)
    const currentYear = getYear(current);
    const nextYear = currentYear + 1;
    const holidayList = [
      ...getGreekHolidays(currentYear),
      ...getGreekHolidays(nextYear),
    ];

    while (added < daysToAdd) {
      const newCurrent = addDays(current, 1);

      const isHoliday = holidayList.some((h) => { return isSameDay(newCurrent, h); });
      const isWeekendDay = isWeekend(newCurrent);

      if (!isWeekendDay && !isHoliday) {
        added += 1;
      }
      current = newCurrent;
    }

    return current;
  }

  if (sampleReceivedDate !== null && sampleReceivedDate !== undefined && sampleReceivedDate !== '') {
    const finalDate = addWorkingDays(sampleReceivedDate, additionalDays);
    const result = finalDate.toLocaleDateString('en-GB');
    return result;
  }
  return '';
}

function ManagerRegistrationsPage() {
  const classes = useStyles();
  const [ filterQuery, setFilterQuery ] = useState({});
  const [ country, setCountry ] = useState('GR');
  const { userHasRole, role } = usePermissions();
  const [ searchSerial, setSearchSerial ] = useState('');
  const [ openDialog, setOpenDialog ] = useState(false);
  const [ selected, setSelected ] = useState({});
  const [ thematic, setThematic ] = useState('All');

  // Select Country
  useEffect(() => {
    if (userHasRole(roles.AL_MANAGER)) {
      setCountry('AL');
    } else if (userHasRole(roles.ADMINISTRATOR_ROLE)) {
      setCountry('GR');
    }
    refetch();
  }, [ role ]);

  // get kits

  const {
    countFiltered,
    items: kits,
    itemsLoading: kitsLoading,
    refetch,
    itemsFetching,
    itemsError: kitsError,
    pagination,
  } = useItems(CollectionKeys.REGISTRATION_KITS, { filterQuery: {
    'serial_number_id.country_eq': country,
    ...filterQuery,
  },
  queryOptions: { refetchOnWindowFocus: false } });

  if (kitsError) {
    return (
      <span>
        Error:
        {kitsError}
      </span>
    );
  }

  // Download
  const downloadData = () => {
    function fitToColumn(arrayOfArray) {
      if (!arrayOfArray[0]) { // If file is empty
        return null;
      }
      return Object.keys(arrayOfArray[0]).map((key) => {
        return { wch: key.length };
      });
    }

    // Get Data
    axiosClient.get(CollectionKeys.REGISTRATION_KITS, { params: {
      'serial_number_id.country_eq': country,
      _limit: -1,
      ...filterQuery,
    } }).then(async (allKits) => {
      if (allKits == null || allKits.length === 0) {
        return;
      }
      const data = allKits.map((kit) => {
        // transform invitations array to object
        let invitations = ' ';
        if (kit?.invitations != null && Array.isArray(kit?.invitations)) {
          const arr = kit?.invitations || [];
          invitations = arr.map((inv) => {
            return (`${ inv?.reason } \t ${ inv?.metadata?.firstName } \t ${ inv?.metadata?.lastName } \t ${ inv?.status }`);
          }).join(' | ');
        }

        const obj = {
          'Serial Number': kit?.serial_number_id?.serial,
          'Report Language': kit?.locale === 3 ? 'en-US' : kit?.locale === 2 ? 'el-GR' : '' || '',
          'Registration Type': kit?.doctor !== null ? 'Doctor ' : kit?.business !== null ? 'Corporate' : 'Individual',
          'Registration Entity': (kit?.doctor !== null ? `${ kit?.doctor?.name }  ${ kit?.doctor?.family_name }` : kit?.business !== null ? `${ kit?.business?.brand_name }` : '') || '',
          'Entity Email': (kit?.doctor !== null ? kit?.doctor?.email : kit?.business !== null ? kit?.business?.email : '') || '',
          'Registration Status': getFullRegistrationStatus(kit),
          'Registartion completion date': parseDate(kit?.metadata?.registrationDoneDate),
          'Date Of Sample Collection': parseDate(kit?.metadata?.sampleCollectionDate),
          'Sample Received Date': parseDate(kit?.metadata?.sampleReceivedDate),
          'Planned Report Delivery Date': deliveryDate(kit),
          Invitations: invitations,

        };
        return obj;
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      worksheet['!cols'] = fitToColumn(data);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      XLSX.writeFile(workbook, 'Kits Reports.xlsx');
    });
  };

  const handleThematic = (value) => {
    setThematic(value);
    if (value !== 'All') {
      setFilterQuery({ ...filterQuery, 'thematic_package.real_name_eq': value });
    } else {
      delete filterQuery['thematic_package.real_name_eq'];
      setFilterQuery(filterQuery);
    }
    refetch();
  };

  const handleRequestSort = (property) => {
    const isAsc = pagination.sort.orderBy === property && pagination.sort.order === 'asc';
    pagination.setSort(property, isAsc ? 'desc' : 'asc');
    pagination.setPage(0);
  };

  const handleSearch = (value) => {
    const newFilter = { 'serial_number_id.serial_contains': value };
    setFilterQuery({ ...filterQuery, ...newFilter });
    refetch();
  };

  // Start search with Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setSearchSerial(e.target.value);
      handleSearch(searchSerial);
    }
  };

  return (
    <Grid container spacing={ 2 } alignItems='center' style={ { backgroundColor: '#ffffff' } }>
      <Grid item xs={ 12 }>
        <div className={ classes.title }>
          Registration Info
        </div>
        <div className={ classes.subtitle }>
          Entries:
          {' '}
          {countFiltered}
        </div>
      </Grid>
      {userHasRole(roles.ADMINISTRATOR_ROLE)
      && (
        <Grid item xs={ 6 } md={ 2 } display='flex'>
          <FormControl style={ { width: '100%' } } className={ classes.selectLocation }>
            <Typography
              variant='subtitle'
              id='location-select-label'
            >
              Location:
            </Typography>
            <Select
              variant='outlined'
              size='small'
              labelId='country-select-label'
              id='country-select'
              value={ country }
              onChange={ (e) => { setCountry(e.target.value); refetch(); } }
            >
              <MenuItem value='GR'>Greece</MenuItem>
              <MenuItem value='AL'>Albania</MenuItem>
              <MenuItem value='IN'>India</MenuItem>
              <MenuItem value='IQ'>Iraq</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={ 6 } md={ 2 } display='flex'>
        <FormControl style={ { width: '100%' } } className={ classes.selectLocation }>
          <Typography
            variant='subtitle'
            id='thematic-select-label'
          >
            Thematic Package:
          </Typography>
          <Select
            variant='outlined'
            size='small'
            labelId='thematic-select-label'
            id='thematic-select'
            value={ thematic }
            onChange={ (e) => {
              handleThematic(e.target.value);
            } }
          >
            <MenuItem value='All'>All</MenuItem>
            <MenuItem value='iDNA PGx CNS Kit'>iDNA PGx kit</MenuItem>
            {country === 'GR' && <MenuItem value='iDNA Test Vitamins & Minerals'>Vitamins kit</MenuItem>}
            <MenuItem value='iDNA Cardio Health'>iDNA Cardio Health kit</MenuItem>
            <MenuItem value='NutriGenetix Laboratories kit'>NutriGenetix Laboratories kit</MenuItem>
            {country === 'GR' && <MenuItem value='iDNA NutriGenetix kit'>NutriGenetix kit</MenuItem>}
            {country === 'GR' && <MenuItem value='iDNA Nutrition Test'>Nutritional kit</MenuItem>}
            {country === 'GR' && <MenuItem value='iDNA Test 3in1'>3-in-1 kit</MenuItem>}
            {country === 'GR' && <MenuItem value='iDNA Biological Age'>Biological Age kit</MenuItem>}

          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={ 6 } md={ 2 } display='flex'>
        <TextField
          className={ classes.searchBox }
          id='search-bar'
          size='medium'
          value={ searchSerial }
          onChange={ (e) => { setSearchSerial(e.target.value); } }
          onKeyPress={ (e) => { handleKeyPress(e); } }
          label='Enter Serial Number'
          variant='outlined'
        />
      </Grid>
      <Grid item xs={ 6 } display='flex'>
        <Button
          className={ classes.button }
          variant='outlined'
          size='large'
          onClick={ () => { handleSearch(searchSerial); } }
        >
          <SearchIcon />
          SEARCH
        </Button>
        { searchSerial.length > 0
      && (
        <Button
          className={ classes.button }
          variant='outlined'
          size='large'
          onClick={ () => {
            setSearchSerial('');
            setFilterQuery({});
          } }
        >
          CLEAR
        </Button>
      )}
        <Button
          className={ classes.button }
          variant='outlined'
          size='large'
          onClick={ downloadData }
        >
          Download
        </Button>
      </Grid>
      <Grid item xs={ 12 } md={ 12 }>
        <Paper>
          <TableContainer className={ classes.tableContainer }>
            { (kitsLoading || itemsFetching) ? <AppLoader />
              : (
                <Table className={ classes.table }>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        className={ classes.headerCell }
                        sortDirection={ pagination.sort.orderBy === 'serial_number_id.serial' ? pagination.sort.order : false }
                      >
                        <TableSortLabel
                          active={ pagination.sort.orderBy === 'serial_number_id.serial' }
                          direction={ pagination.sort.orderBy === 'serial_number_id.serial' ? pagination.sort.order : 'asc' }
                          onClick={ () => { return handleRequestSort('serial_number_id.serial'); } }
                        >
                          Serial Number
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={ classes.headerCell }>Registration Entity</TableCell>
                      <TableCell className={ classes.headerCell }>Registration Status</TableCell>
                      <TableCell className={ classes.headerCell }>Sample Received Date</TableCell>
                      <TableCell className={ classes.headerCell }>
                        Planned Report
                        <br />
                        Delivery Date
                      </TableCell>
                      <TableCell className={ classes.headerCell }>More Info</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {kits.map((kit) => {
                      return (
                        <TableRow key={ kit.id } className={ classes.row }>
                          <TableCell>{kit?.serial_number_id?.serial}</TableCell>
                          <TableCell>{kit?.doctor !== null ? 'Doctor ' : kit?.business !== null ? 'Diagnostic Center' : 'Individual'}</TableCell>
                          <TableCell>{getFullRegistrationStatus(kit)}</TableCell>
                          <TableCell>{parseDate(kit?.metadata?.sampleReceivedDate)}</TableCell>
                          <TableCell>{deliveryDate(kit)}</TableCell>
                          <TableCell>
                            <Button
                              className={ classes.infoButton }
                              onClick={ () => {
                                setSelected(kit);
                                setOpenDialog(true);
                              } }
                            >
                              More Info
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
          </TableContainer>

          <TablePagination
            component='div'
            count={ countFiltered }
            page={ pagination.page }
            onChangePage={ (e, p) => {
              pagination.setPage(p);
            } }
            rowsPerPage={ pagination.limit > 0 ? pagination.limit : 10 }
            onChangeRowsPerPage={
              (e) => {
                const newLimit = parseInt(e.target.value, 10);
                pagination.setLimit(newLimit);
                pagination.setPage(0);
              }
            }
            rowsPerPageOptions={ [ 5, 10, 25 ] }
          />
        </Paper>
        <Dialog
          open={ openDialog }
          onClose={ () => { setSelected({}); setOpenDialog(false); } }
          classes={ { paper: classes.dialogPaper } }
        >
          <DialogContent className={ classes.dialogContent }>
            <Typography>
              <DialogContentText>
                <ManagersRegistrationsDetails kit={ selected } />
              </DialogContentText>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              className={ classes.button }
              onClick={ () => {
                setSelected({});
                setOpenDialog(false);
              } }
              autoFocus
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
}

export default ManagerRegistrationsPage;
